import {
  CheckCircle2,
  FolderOpen,
  Monitor,
  Search,
  Utensils,
} from "lucide-react";
import { twJoin } from "tailwind-merge";

import type { CategoryPaginatedItem, ItemWithRelations } from "~/api-refs";
import { isRoutingMode } from "~/components/stations/station-edit-types";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "~/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { parseEnumValue } from "~/lib/parse-enum-value";

import type { StationColorType } from "./station-wizard-types";

export const StepIndicator = ({
  currentStep,
  totalSteps,
}: {
  currentStep: number;
  totalSteps: number;
}) => (
  <div className="flex gap-2">
    {Array.from({ length: totalSteps }).map((_, step) => (
      <div
        className={twJoin(
          "h-1.5 flex-1 rounded-full",
          step <= currentStep ? "bg-primary" : "bg-muted"
        )}
        key={`step-indicator-${step.toString()}`}
      />
    ))}
  </div>
);

export const StepBasicInfo = ({
  name,
  setName,
  hasScreen,
  setHasScreen,
  routingMode,
  setRoutingMode,
  color,
  errorMessage,
}: {
  name: string;
  setName: (name: string) => void;
  hasScreen: boolean;
  setHasScreen: (value: boolean) => void;
  routingMode: "all" | "categories" | "items";
  setRoutingMode: (mode: "all" | "categories" | "items") => void;
  color: StationColorType;
  errorMessage: string;
}) => (
  <div className="flex flex-col gap-5">
    <div className="flex flex-col items-center">
      <h1 className="h4">Create Station</h1>
      <p className="body text-muted-foreground">
        A station is a place in your kitchen (e.g., Grill, Bar, Expo)
      </p>
    </div>

    {/* Preview Card */}
    <div className="flex flex-col items-center">
      <div
        className="flex h-16 w-48 items-center justify-center rounded-xl text-center font-medium"
        style={{ background: color.color, stroke: color.stroke }}
      >
        {name || "Station Name"}
      </div>
    </div>

    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="body font-medium">Station Name</p>
        <Input
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Grill, Bar, Expo..."
          value={name}
        />
      </div>

      <button
        className="bg-accent flex cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-left"
        onClick={() => setHasScreen(!hasScreen)}
        type="button"
      >
        <div className="flex items-center gap-3">
          <Monitor className="h-5 w-5" />
          <div>
            <p className="font-medium">Has KDS Screen</p>
            <p className="text-muted-foreground text-sm">
              Orders will appear on a display
            </p>
          </div>
        </div>
        <Switch checked={hasScreen} />
      </button>

      <div className="flex flex-col gap-2">
        <p className="body font-medium">Routing Mode</p>
        <Select
          onValueChange={(v) =>
            setRoutingMode(parseEnumValue(v, isRoutingMode, "all"))
          }
          value={routingMode}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items (receives everything)</SelectItem>
            <SelectItem value="categories">Filter by Categories</SelectItem>
            <SelectItem value="items">Filter by Specific Items</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {routingMode === "all"
            ? "This station will receive all orders"
            : `You'll select ${routingMode} in the next step`}
        </p>
      </div>

      {errorMessage && <p className="text-destructive">{errorMessage}</p>}
    </div>
  </div>
);

export const StepRouting = ({
  name,
  routingMode,
  searchValue,
  setSearchValue,
  categories,
  items,
  categoriesSelected,
  itemsSelected,
  toggleSelection,
  color,
}: {
  name: string;
  routingMode: "categories" | "items";
  searchValue: string;
  setSearchValue: (value: string) => void;
  categories: CategoryPaginatedItem[];
  items: ItemWithRelations[];
  categoriesSelected: string[];
  itemsSelected: string[];
  toggleSelection: (id: string, type: "category" | "item") => void;
  color: StationColorType;
}) => (
  <div className="flex flex-col gap-5">
    <div className="flex flex-col items-center">
      <h1 className="h4">
        Select {routingMode === "categories" ? "Categories" : "Items"}
      </h1>
      <p className="body text-muted-foreground">
        Choose which {routingMode} this station will handle
      </p>
    </div>

    <div className="flex flex-col items-center">
      <div
        className="flex h-12 w-40 items-center justify-center rounded-xl text-center font-medium"
        style={{ background: color.color, stroke: color.stroke }}
      >
        {name}
      </div>
    </div>

    <InputGroup>
      <InputGroupAddon align="inline-start">
        <Search className="text-foreground size-4" />
      </InputGroupAddon>
      <InputGroupInput
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder={`Search ${routingMode}...`}
        value={searchValue}
      />
    </InputGroup>

    <div className="max-h-80 overflow-y-auto">
      {routingMode === "categories" ? (
        <div className="flex flex-col gap-2">
          {categories
            ?.filter((cat) =>
              cat.name.toLowerCase().includes(searchValue.toLowerCase())
            )
            .map((category) => (
              <button
                className={twJoin(
                  "flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-3 text-left",
                  categoriesSelected.includes(category._id)
                    ? "bg-accent"
                    : "hover:bg-accent/50"
                )}
                key={category._id}
                onClick={() => toggleSelection(category._id, "category")}
                type="button"
              >
                <FolderOpen className="text-category h-5 w-5" />
                <div className="flex-1">
                  <p className="font-medium">{category.name}</p>
                  <p className="text-muted-foreground text-sm">
                    {category.itemCount} items
                  </p>
                </div>
                {categoriesSelected.includes(category._id) && (
                  <CheckCircle2 className="text-primary h-5 w-5" />
                )}
              </button>
            ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items
            ?.filter((item) =>
              item.name.toLowerCase().includes(searchValue.toLowerCase())
            )
            .map((item) => (
              <button
                className={twJoin(
                  "flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-3 text-left",
                  itemsSelected.includes(item._id)
                    ? "bg-accent"
                    : "hover:bg-accent/50"
                )}
                key={item._id}
                onClick={() => toggleSelection(item._id, "item")}
                type="button"
              >
                <Utensils className="text-item h-5 w-5" />
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                </div>
                {itemsSelected.includes(item._id) && (
                  <CheckCircle2 className="text-primary h-5 w-5" />
                )}
              </button>
            ))}
        </div>
      )}
    </div>

    <p className="text-muted-foreground text-center text-sm">
      {routingMode === "categories"
        ? `${categoriesSelected.length} categories selected`
        : `${itemsSelected.length} items selected`}
    </p>
  </div>
);
